import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaRobot, FaTimes, FaPaperPlane, FaMinus, FaExpand, FaArrowLeft, FaHeadset } from 'react-icons/fa';
import { useChat } from '../../../contexts/ChatContext';
import { useMessages } from '../../../contexts/MessageContext';
import useEnhancedDeviceDetection from '../../../hooks/useEnhancedDeviceDetection';
import { useAuth } from '../../../contexts/AuthContext';
import ContactForm from './ContactFormNew';
import './ChatWindow.css';

// Format timestamp for messages
const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const ChatWindow = () => {
  // Get all contexts and hooks FIRST
  const { 
    isOpen, 
    toggleChatWindow, 
    messages, 
    setMessages,
    isSending, 
    sendMessage: sendChatMessage,
    showContactForm: contextShowForm,
    setShowContactForm: setContextShowForm
  } = useChat();
  
  const { createConversation, sendMessage: sendThreadMessage, fetchThreadById } = useMessages();
  const { currentUser, userRole } = useAuth();
  const { isMobile, isTablet } = useEnhancedDeviceDetection();
  
  // State management - ALL hooks must be declared before any early returns
  const [inputMessage, setInputMessage] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isAdminChat, setIsAdminChat] = useState(false);
  const [adminConversation, setAdminConversation] = useState(null);
  const [messageHistory, setMessageHistory] = useState(messages || []);
  const [adminRequestMessage, setAdminRequestMessage] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  // ALL useEffect and useCallback hooks must come before early return
  // Update local messages when context messages change
  useEffect(() => {
    setMessageHistory(messages);
  }, [messages]);
  
  // Handle admin request detection
  useEffect(() => {
    if (Array.isArray(messages)) {
      const adminRequest = messages.find(msg => 
        msg.sender === 'user' && (
          msg.text.toLowerCase().includes('talk to an admin') ||
          msg.text.toLowerCase().includes('speak to human') ||
          msg.text.toLowerCase().includes('contact admin') ||
          msg.text.toLowerCase().includes('human agent')
        )
      );
      
      if (adminRequest) {
        setAdminRequestMessage(adminRequest);
      }
    }
  }, [messages]);
  
  // Update local state when context changes
  useEffect(() => {
    if (contextShowForm !== showContactForm) {
      setShowContactForm(contextShowForm);
    }
  }, [contextShowForm, showContactForm]);
  
  // Handle sending a message
  const handleSendMessage = useCallback(async (e) => {
    e?.preventDefault();
    
    const message = inputMessage.trim();
    if (!message) return;
    
    if (isAdminChat && adminConversation) {
      // Send via MessageContext for real-time admin chat
      await sendThreadMessage(adminConversation.id, message);
      // Refetch to get the updated thread
      const updated = await fetchThreadById(adminConversation.id);
      if (updated) setAdminConversation(updated);
    } else {
      // Send via ChatContext for bot chat
      await sendChatMessage(message);
      // Simulate bot response after a short delay
      setIsTyping(true);
      setTimeout(() => setIsTyping(false), 1000);
    }
    
    setInputMessage('');
    scrollToBottom();
  }, [inputMessage, isAdminChat, adminConversation, sendChatMessage, sendThreadMessage, fetchThreadById]);
  
  // Handle Enter key press (but allow Shift+Enter for new lines)
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  }, [handleSendMessage]);
  
  // Handle close button click - fully close the chat
  const handleCloseChat = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Close the chat window using the context function
    toggleChatWindow();
    setIsMinimized(false);
    setContextShowForm(false);
    // Reset admin chat state when closing
    setIsAdminChat(false);
    setAdminConversation(null);
  }, [toggleChatWindow, setContextShowForm]);
  
  // Toggle minimize/expand
  const toggleMinimize = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newMinimized = !isMinimized;
    setIsMinimized(newMinimized);
    
    // If un-minimizing, ensure chat is open
    if (newMinimized === false && !isOpen) {
      toggleChatWindow();
    }
  }, [isMinimized, isOpen, toggleChatWindow]);
  
  // Toggle expand/collapse
  const toggleExpand = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsExpanded(prev => !prev);
    // If expanding, un-minimize
    if (isMinimized) {
      setIsMinimized(false);
    }
  }, [isMinimized]);
  
  // Scroll to bottom of messages
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);
  
  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isAtBottom = scrollHeight - (scrollTop + clientHeight) < 50;
      setShowScrollToBottom(!isAtBottom);
    }
  }, []);
  
  // Auto-scroll when new messages arrive or chat is opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom('auto');
    }
  }, [messages, isOpen, isMinimized, scrollToBottom]);
  
  // Auto-scroll when admin conversation updates
  useEffect(() => {
    if (adminConversation && isOpen && !isMinimized) {
      scrollToBottom('auto');
    }
  }, [adminConversation, isOpen, isMinimized, scrollToBottom]);
  
  // Add scroll event listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);
  
  // Don't show chat window for admin users - check multiple conditions
  const isAdmin = userRole === 'admin' || 
                   currentUser?.role === 'admin' || 
                   currentUser?.is_staff || 
                   currentUser?.is_superuser;
  
  if (isAdmin) {
    return null;
  }
  
  // Rest of component rendering...
  
  // Don't render if chat is closed and not minimized
  if (!isOpen && !isMinimized) {
    return null;
  }
  
  return (
    <div className={`chat-window ${isExpanded ? 'expanded' : ''} ${isMinimized ? 'minimized' : ''} ${isOpen ? 'open' : ''}`}>
      <div className="chat-header">
        <div className="chat-header-content" onClick={toggleMinimize}>
          {showContactForm && (
            <button 
              className="back-button"
              onClick={(e) => {
                e.stopPropagation();
                setContextShowForm(false);
              }}
              aria-label="Back to chat"
            >
              <FaArrowLeft />
            </button>
          )}
          <div className="chat-title">
            {!showContactForm && <FaRobot className="chat-icon" />}
            <span>{showContactForm ? 'Contact Support' : isAdminChat ? 'Admin Support' : 'UniTrade Assistant'}</span>
          </div>
          <div className="chat-actions">
            <button 
              className="chat-action-btn" 
              onClick={toggleMinimize}
              aria-label={isMinimized ? 'Maximize chat' : 'Minimize chat'}
            >
              {isMinimized ? <FaExpand /> : <FaMinus />}
            </button>
            <button 
              className="chat-action-btn close-btn"
              onClick={handleCloseChat}
              aria-label="Close chat"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      </div>
      
      {!isMinimized && (
        <>
          <div className="chat-content">
            {showContactForm ? (
              <div className="chat-contact-form">
                <ContactForm 
                  onCancel={() => setContextShowForm(false)}
                  onSuccess={() => {
                    setContextShowForm(false);
                    // Add a thank you message to the chat
                    const thankYouMessage = {
                      id: 'msg_' + Date.now(),
                      text: "Thank you for contacting us! We'll get back to you as soon as possible.",
                      sender: 'bot',
                      timestamp: new Date().toISOString()
                    };
                    // Update messages with the thank you message
                    setMessages(prev => [...prev, thankYouMessage]);
                  }}
                  email={currentUser?.email}
                />
              </div>
            ) : (
              <div className="chat-messages" ref={messagesContainerRef}>
                {(isAdminChat ? [...(messageHistory), adminRequestMessage].filter(Boolean) : messageHistory).map((message, index) => {
                  // Create a truly unique key using multiple factors
                  const uniqueKey = message.id 
                    ? `${message.id}_${index}_${message.timestamp || Date.now()}` 
                    : `msg_${index}_${message.timestamp || Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                  
                  return (
                    <div
                      key={uniqueKey}
                      className={`message ${isAdminChat ? (message.sender === currentUser?.id ? 'user' : 'admin') : message.sender}`}
                    >
                      <div className="message-content">
                        {message.content || message.text}
                        <span className="message-time">
                          {formatTime(message.timestamp)}
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
            
            {!showContactForm && (
              <form 
                className="chat-input-container"
                onSubmit={handleSendMessage}
              >
                <input
                  type="text"
                  className="chat-input"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  disabled={isSending}
                  aria-label="Type your message"
                />
                <button 
                  type="submit" 
                  className="send-button"
                  disabled={!inputMessage.trim() || isSending}
                  aria-label="Send message"
                >
                  {isSending ? (
                    <Spinner animation="border" size="sm" aria-hidden="true" />
                  ) : (
                    <FaPaperPlane aria-hidden="true" />
                  )}
                </button>
              </form>
            )}
            
            {!showContactForm && (
              <div className="chat-admin-request-container p-2 border-top">
                <button
                  className="chat-admin-request-btn w-100"
                  onClick={async () => {
                    if (!currentUser) {
                      // Show login prompt if not authenticated
                      const loginMessage = {
                        id: 'msg_' + Date.now(),
                        text: "Please login to talk to an admin.",
                        sender: 'bot',
                        timestamp: new Date().toISOString()
                      };
                      setMessages(prev => [...prev, loginMessage]);
                      return;
                    }
                    
                    try {
                      // For now, let's add a message to current chat instead of creating admin conversation
                      // This simulates admin connection without requiring actual admin user
                      const newAdminRequestMessage = {
                        id: 'msg_' + Date.now(),
                        text: "An admin has been notified of your request. They will join this chat shortly to assist you.",
                        sender: 'bot',
                        timestamp: new Date().toISOString()
                      };
                      
                      setMessages(prev => [...prev, newAdminRequestMessage]);
                      setAdminRequestMessage(newAdminRequestMessage);
                      
                      // Switch to admin mode (simulated)
                      setIsAdminChat(true);
                      
                      // In a real implementation, you would:
                      // 1. Create conversation with actual admin ID
                      // 2. Send message to admin notification system
                      // 3. Connect to real admin chat interface
                      
                    } catch (error) {
                      console.error('Error starting admin chat:', error);
                      // Show error message instead of fallback to contact form
                      const errorMessage = {
                        id: 'msg_' + Date.now(),
                        text: "Sorry, unable to connect with admin. Please try again later.",
                        sender: 'bot',
                        timestamp: new Date().toISOString()
                      };
                      setMessages(prev => [...prev, errorMessage]);
                    }
                  }}
                  disabled={isSending}
                >
                  <FaHeadset className="me-2" />
                  Talk to an admin
                </button>
              </div>
            )}
          </div>
          
          {showScrollToBottom && (
            <button 
              className="scroll-to-bottom"
              onClick={() => scrollToBottom('smooth')}
              aria-label="Scroll to bottom"
            >
              ↓
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ChatWindow;
